<template>
    <v-dialog
        :retain-focus="false"
        v-model="show"
        persistent
        max-width="1800px"
        :fullscreen="$vuetify.breakpoint.mobile"
    >
        <v-card :loading="loading">
            <v-card-title class="text-h5">
                <span>Calculator Items</span>
                <span
                    v-if="importProgress"
                    class="text-subtitle-1 font-weight-regular ml-2"
                >
                    {{ importProgress.current }} / {{ importProgress.total }}
                </span>
                <v-spacer></v-spacer>
                <v-btn
                    v-if="canEdit"
                    icon
                    color="info"
                    @click="downloadTemplate"
                    title="Download template"
                >
                    <v-icon>mdi-file-download</v-icon>
                </v-btn>
                <v-btn
                    v-if="canEdit"
                    icon
                    color="primary"
                    @click="triggerFileInput"
                    :loading="uploadingFile"
                    title="Import items from XLSX"
                >
                    <v-icon>mdi-file-upload</v-icon>
                </v-btn>
                <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    style="display: none"
                    @change="handleFileUpload"
                />
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text>
                <v-text-field
                    v-model="nameFilter"
                    dense
                    hide-details
                    clearable
                    label="Filter by name"
                    prepend-inner-icon="mdi-magnify"
                    class="mb-3 name-filter"
                />
                <div class="calculator-items-table">
                    <table>
                        <thead>
                            <tr>
                                <th
                                    :colspan="scheduleColspan"
                                    class="schedule-title"
                                >
                                    Louvers Schedule
                                </th>
                            </tr>
                            <tr class="schedule-summary">
                                <th colspan="2">
                                    Openings (qty)<br />
                                    {{ formatNumber(totalOpenings) }}
                                </th>
                                <th colspan="2">
                                    Louvers (Qty)<br />
                                    {{ formatNumber(totalLouvers) }}
                                </th>
                                <th colspan="2">
                                    Total Area (SqFt)<br />
                                    {{ formatNumber(totalAreaSqft) }}
                                </th>
                                <th
                                    :colspan="messageColspan"
                                    class="schedule-message-cell"
                                >
                                    <span class="mr-2">{{
                                        localScheduleMessage || '--'
                                    }}</span>
                                    <v-btn
                                        v-if="canEdit"
                                        icon
                                        x-small
                                        color="primary"
                                        title="Edit schedule message"
                                        @click="startScheduleEdit"
                                    >
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                </th>
                            </tr>
                            <tr class="schedule-groups">
                                <th colspan="6">Opening Dimension</th>
                                <th :colspan="modulesColspan">
                                    Modules Dimension
                                </th>
                                <th colspan="3"></th>
                            </tr>
                            <tr>
                                <th>Reference</th>
                                <th>Model</th>
                                <th>Opening Quantity (qty)</th>
                                <th>Opening Width (In)</th>
                                <th>Opening Height (In)</th>
                                <th>Louver Quantity</th>
                                <th>Louver Width (In)</th>
                                <th>Louver Height (In)</th>
                                <th>Area (sqft)</th>
                                <th>Stiffener</th>
                                <th v-if="showVerticalMullion">
                                    Vertical Mullion
                                </th>
                                <th v-if="showHorizontalMullion">
                                    Horizontal Mullion
                                </th>
                                <th>Mesh</th>
                                <th>Blank off Panel</th>
                                <th>Back Pan</th>
                                <th>Sill Pan</th>
                                <th>Vertical Division</th>
                                <th>Horizontal Division</th>
                                <th>Open Area (%)</th>
                                <th>Weight (lbs)</th>
                                <th>Net Each Price</th>
                                <th>Total Price</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in pagedItems" :key="item.id">
                                <td class="text-left">
                                    {{ item.lineItemName || '--' }}
                                </td>
                                <td>
                                    {{
                                        item.presentationModel ||
                                            item.model ||
                                            quoteModel ||
                                            '--'
                                    }}
                                </td>
                                <td>{{ formatNumber(item.quantity) }}</td>
                                <td>{{ formatNumber(item.width) }}</td>
                                <td>{{ formatNumber(item.height) }}</td>
                                <td>{{ formatNumber(item.louverQuantity) }}</td>
                                <td>{{ formatNumber(item.louverWidth) }}</td>
                                <td>{{ formatNumber(item.louverHeight) }}</td>
                                <td>{{ formatNumber(areaSqft(item)) }}</td>
                                <td>{{ stiffener(item) }}</td>
                                <td v-if="showVerticalMullion">
                                    {{ verticalMullion(item) }}
                                </td>
                                <td v-if="showHorizontalMullion">
                                    {{ horizontalMullion(item) }}
                                </td>
                                <td>{{ yesNo(item.birdScreen) }}</td>
                                <td>{{ yesNo(item.blankOffPanel) }}</td>
                                <td>{{ yesNo(item.backPan) }}</td>
                                <td>{{ yesNo(item.sillPan) }}</td>
                                <td>{{ verticalDivision(item) }}</td>
                                <td>{{ horizontalDivision(item) }}</td>
                                <td>{{ formatNumber(openAreaPct(item)) }}</td>
                                <td>{{ formatLouverWeight(item) }}</td>
                                <td>
                                    <editable-cell
                                        :item="item"
                                        field="netEachPrice"
                                        kind="price"
                                        :can-edit="canEdit"
                                        :editing-id="editing.id"
                                        :editing-field="editing.field"
                                        :editing-value="editingValue"
                                        :saving="
                                            loading && editingItemId === item.id
                                        "
                                        :display-text="formatPrice(item)"
                                        :currency="currency"
                                        @edit="startEdit(item, 'netEachPrice')"
                                        @save="saveField(item, $event)"
                                        @cancel="cancelEdit"
                                    />
                                </td>
                                <td>
                                    {{
                                        getTotalPrice(item)
                                            ? new Intl.NumberFormat('de-DE', {
                                                  style: 'currency',
                                                  currency: currency || 'USD',
                                                  maximumFractionDigits: 2,
                                              }).format(getTotalPrice(item))
                                            : '--'
                                    }}
                                </td>
                                <td class="text-nowrap">
                                    <v-btn
                                        v-if="canEdit"
                                        icon
                                        x-small
                                        color="primary"
                                        title="Edit item"
                                        @click="openRowEdit(item)"
                                    >
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-if="canEdit"
                                        icon
                                        x-small
                                        color="primary"
                                        title="Delete item"
                                        :loading="deletingItemId === item.id"
                                        @click="askDelete(item)"
                                    >
                                        <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-if="!filteredItems.length && !loading">
                                <td
                                    :colspan="scheduleColspan"
                                    class="text-center py-4"
                                >
                                    {{
                                        calculatorItems.length
                                            ? 'No matches'
                                            : 'No items'
                                    }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="pageCount > 1" class="d-flex justify-center mt-3">
                    <v-pagination
                        v-model="page"
                        :length="pageCount"
                        :total-visible="7"
                    />
                </div>
                <div class="d-flex justify-end mt-4">
                    <v-btn
                        v-if="canEdit"
                        color="primary"
                        rounded
                        class="text-none"
                        @click="openRowCreate"
                    >
                        ADD ITEM
                    </v-btn>
                </div>
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-end align-center pa-2">
                    <span class="text-h6 font-weight-bold mr-4">Subtotal:</span>
                    <span class="text-h6 font-weight-bold">
                        {{
                            subtotal
                                ? new Intl.NumberFormat('de-DE', {
                                      style: 'currency',
                                      currency: currency || 'USD',
                                      maximumFractionDigits: 2,
                                  }).format(subtotal)
                                : '--'
                        }}
                    </span>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="secondary" @click="closeDialog" rounded>
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
        <calculator-item-edit-dialog
            v-model="rowEditShow"
            :item="rowEditItem"
            :saving="rowSaving"
            :currency="currency"
            @save="saveRow"
        />
        <v-dialog
            v-model="scheduleMessageShow"
            persistent
            max-width="560px"
            :retain-focus="false"
        >
            <v-card :loading="scheduleMessageSaving">
                <v-card-title class="text-h6">Schedule message</v-card-title>
                <v-card-text>
                    <v-textarea
                        v-model="scheduleMessageDraft"
                        rows="4"
                        auto-grow
                        hide-details
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        rounded
                        color="secondary"
                        :disabled="scheduleMessageSaving"
                        @click="cancelScheduleEdit"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        rounded
                        color="primary"
                        :loading="scheduleMessageSaving"
                        @click="saveScheduleMessage"
                    >
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="deleteConfirmShow"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="!!deletingItemId">
                <v-card-title class="text-h6">Delete item</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this item?
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        rounded
                        color="secondary"
                        :disabled="!!deletingItemId"
                        @click="cancelDelete"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        rounded
                        color="error"
                        :loading="!!deletingItemId"
                        @click="confirmDelete"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="xlsxMismatchDialog"
            persistent
            max-width="520px"
            :retain-focus="false"
        >
            <v-card :loading="uploadingFile">
                <v-card-title class="text-h6">Subtotal mismatch</v-card-title>
                <v-card-text>
                    The subtotal from the uploaded items does not match the
                    existing costs subtotal.
                    <br /><br />
                    <strong>Costs subtotal:</strong>
                    {{ formatCurrency(costsSubtotal) }}
                    <br />
                    <strong>Incoming items subtotal:</strong>
                    {{ formatCurrency(previewSubtotal) }}
                    <br />
                    <strong>Difference:</strong>
                    {{
                        formatCurrency(
                            Math.abs(previewSubtotal - costsSubtotal)
                        )
                    }}
                    <br /><br />
                    Do you want to continue with the import?
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        rounded
                        color="secondary"
                        :disabled="uploadingFile"
                        @click="cancelXlsxImport"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        rounded
                        color="primary"
                        :loading="uploadingFile"
                        @click="confirmXlsxImport"
                    >
                        Continue
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar
            v-model="activateAlert"
            :color="
                alertType === 'error'
                    ? 'error'
                    : alertType === 'warning'
                    ? 'warning'
                    : 'success'
            "
            :timeout="6000"
            top
            right
        >
            {{ alertMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="activateAlert = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-dialog>
</template>

<script>
import API from '@/services/api'
import * as XLSX from 'xlsx/xlsx.mjs'
import {
    EXCEL_HEADERS,
    IMPORT_CHUNK_SIZE,
    PAGE_SIZE,
    parseCalculatorItemsSheet,
    applyCalculatedOpenArea,
    buildCalculatorItemPatch,
    buildCalculatorItemRowPatch,
    templateRowFromItem,
    getLouverWeightLbs,
} from '@/utils/calculatorItemsExcel'
import EditableCell from './CalculatorItemEditableCell.vue'
import CalculatorItemEditDialog from './CalculatorItemEditDialog.vue'

export default {
    name: 'CalculatorItemsDialog',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        quoteId: {
            type: String,
            required: true,
        },
        currency: {
            type: String,
            default: 'USD',
        },
        quoteStatus: {
            type: String,
            default: '',
        },
        quoteModel: {
            type: String,
            default: '',
        },
        scheduleMessage: {
            type: String,
            default: '',
        },
        costsSubtotal: {
            type: Number,
            default: 0,
        },
    },
    components: { EditableCell, CalculatorItemEditDialog },
    data() {
        return {
            loading: false,
            uploadingFile: false,
            calculatorItems: [],
            editing: { id: null, field: null },
            editingValue: '',
            editingItemId: null,
            deletingItemId: null,
            deleteConfirmShow: false,
            pendingDeleteItem: null,
            rowEditShow: false,
            rowEditItem: null,
            rowSaving: false,
            activateAlert: false,
            alertMessage: '',
            alertType: 'success',
            localScheduleMessage: '',
            scheduleMessageShow: false,
            scheduleMessageDraft: '',
            scheduleMessageSaving: false,
            previewItems: [],
            previewSubtotal: 0,
            pendingModel: null,
            xlsxMismatchDialog: false,
            nameFilter: '',
            page: 1,
            pageSize: PAGE_SIZE,
            importProgress: null,
        }
    },
    computed: {
        show: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            },
        },
        canEdit() {
            return (
                this.quoteStatus === 'OPEN' || this.quoteStatus === 'APPROVAL'
            )
        },
        subtotal() {
            return this.calculatorItems.reduce((sum, item) => {
                const totalPrice = this.getTotalPrice(item)
                return sum + (totalPrice || 0)
            }, 0)
        },
        totalOpenings() {
            return this.calculatorItems.reduce(
                (sum, item) => sum + (Number(item.quantity) || 0),
                0
            )
        },
        totalLouvers() {
            return this.calculatorItems.reduce(
                (sum, item) => sum + (Number(item.louverQuantity) || 0),
                0
            )
        },
        totalAreaSqft() {
            return this.calculatorItems.reduce(
                (sum, item) => sum + this.areaSqft(item),
                0
            )
        },
        mullionColumns() {
            switch (this.quoteModel) {
                case '5LS-H':
                case '1.8LS-H':
                    return { vertical: true, horizontal: false }
                case '5LS-V':
                case '3LS-V':
                    return { vertical: false, horizontal: true }
                case '5LS-HV':
                case '1.4LS-42':
                case '1.4LS-42-HP':
                case '1.4LS-60':
                    return { vertical: true, horizontal: true }
                default:
                    return { vertical: true, horizontal: false }
            }
        },
        showVerticalMullion() {
            return this.mullionColumns.vertical
        },
        showHorizontalMullion() {
            return this.mullionColumns.horizontal
        },
        scheduleColspan() {
            return (
                21 +
                (this.showVerticalMullion ? 1 : 0) +
                (this.showHorizontalMullion ? 1 : 0)
            )
        },
        messageColspan() {
            return (
                15 +
                (this.showVerticalMullion ? 1 : 0) +
                (this.showHorizontalMullion ? 1 : 0)
            )
        },
        modulesColspan() {
            return (
                12 +
                (this.showVerticalMullion ? 1 : 0) +
                (this.showHorizontalMullion ? 1 : 0)
            )
        },
        filteredItems() {
            const q = String(this.nameFilter || '').trim().toLowerCase()
            if (!q) return this.calculatorItems
            return this.calculatorItems.filter(item =>
                String(item.lineItemName || '')
                    .toLowerCase()
                    .includes(q)
            )
        },
        pageCount() {
            return Math.max(
                1,
                Math.ceil(this.filteredItems.length / this.pageSize)
            )
        },
        pagedItems() {
            const start = (this.page - 1) * this.pageSize
            return this.filteredItems.slice(start, start + this.pageSize)
        },
    },
    watch: {
        show(newVal) {
            if (newVal && this.quoteId) {
                this.localScheduleMessage = this.scheduleMessage || ''
                this.nameFilter = ''
                this.page = 1
                this.loadCalculatorItems()
            }
        },
        nameFilter() {
            this.page = 1
        },
        pageCount(n) {
            if (this.page > n) this.page = n
        },
    },
    methods: {
        async loadCalculatorItems() {
            try {
                this.loading = true
                const calculator = await API.getCalculator(this.quoteId)
                const sorted = (calculator || []).slice().sort((a, b) => {
                    const aKey = a.index ?? a.createdOn?._seconds ?? 0
                    const bKey = b.index ?? b.createdOn?._seconds ?? 0
                    return aKey - bKey
                })
                this.calculatorItems = sorted.map(item => ({ ...item }))
                this.editing = { id: null, field: null }
            } catch (error) {
                console.error('Error loading calculator items:', error)
                this.$emit('error', error)
            } finally {
                this.loading = false
            }
        },
        startEdit(item, field) {
            this.editing = { id: item.id, field }
            this.editingValue = this.getDisplayPrice(item)
        },
        cancelEdit() {
            this.editing = { id: null, field: null }
            this.editingValue = ''
            this.editingItemId = null
        },
        startScheduleEdit() {
            this.scheduleMessageDraft = this.localScheduleMessage || ''
            this.scheduleMessageShow = true
        },
        cancelScheduleEdit() {
            if (this.scheduleMessageSaving) return
            this.scheduleMessageShow = false
            this.scheduleMessageDraft = ''
        },
        async saveScheduleMessage() {
            try {
                this.scheduleMessageSaving = true
                const saved = await API.updateScheduleMessage({
                    quoteId: this.quoteId,
                    scheduleMessage: String(
                        this.scheduleMessageDraft || ''
                    ).trim(),
                })
                this.localScheduleMessage = saved != null ? saved : ''
                this.$emit('update:scheduleMessage', this.localScheduleMessage)
                this.scheduleMessageShow = false
                this.scheduleMessageDraft = ''
            } catch (error) {
                console.error('Error updating schedule message:', error)
                this.$emit('error', error)
                this.alertType = 'error'
                this.alertMessage =
                    error.message || 'Error updating schedule message.'
                this.activateAlert = true
            } finally {
                this.scheduleMessageSaving = false
            }
        },
        openRowEdit(item) {
            this.rowEditItem = item
            this.rowEditShow = true
        },
        openRowCreate() {
            this.rowEditItem = null
            this.rowEditShow = true
        },
        askDelete(item) {
            if (!item || !this.canEdit) return
            this.pendingDeleteItem = item
            this.deleteConfirmShow = true
        },
        cancelDelete() {
            if (this.deletingItemId) return
            this.deleteConfirmShow = false
            this.pendingDeleteItem = null
        },
        async confirmDelete() {
            const item = this.pendingDeleteItem
            if (!item) return
            try {
                this.deletingItemId = item.id
                await API.deleteCalculatorItem({
                    quoteId: this.quoteId,
                    itemId: item.id,
                })
                this.calculatorItems = this.calculatorItems.filter(
                    i => i.id !== item.id
                )
                if (this.rowEditItem && this.rowEditItem.id === item.id) {
                    this.rowEditShow = false
                    this.rowEditItem = null
                }
                this.deleteConfirmShow = false
                this.pendingDeleteItem = null
            } catch (error) {
                console.error('Error deleting calculator item:', error)
                this.$emit('error', error)
                this.alertType = 'error'
                this.alertMessage = error.message || 'Error deleting item.'
                this.activateAlert = true
            } finally {
                this.deletingItemId = null
            }
        },
        getDisplayPrice(item) {
            return item.fixedNetUnitPrice != null
                ? item.fixedNetUnitPrice
                : item.netEachPrice
        },
        getTotalPrice(item) {
            const price =
                item.fixedNetUnitPrice != null
                    ? item.fixedNetUnitPrice
                    : item.netEachPrice

            if (price != null && item.louverQuantity) {
                return price * item.louverQuantity
            }
            return item.totalPrice
        },
        formatNumber(value) {
            if (value == null || value === '') return '--'
            const n = Number(value)
            return Number.isNaN(n) ? '--' : n.toFixed(2)
        },
        yesNo(value) {
            return value ? 'YES' : 'NO'
        },
        hModules(item) {
            return Number(item.horizontalModules ?? item.hModules) || 0
        },
        vModules(item) {
            return Number(item.verticalModules ?? item.vModules) || 0
        },
        areaSqft(item) {
            const w = Number(item.louverWidth)
            const h = Number(item.louverHeight)
            const q = Number(item.louverQuantity)
            if (!w || !h || !q) return 0
            return (w * h * q) / 144
        },
        stiffener(item) {
            return Number(item.louverWidth) > 30 ? 'YES' : 'NO'
        },
        verticalMullion(item) {
            return (this.hModules(item) > 1 ? Number(item.height) / 12 : 0) > 0
                ? 'YES'
                : 'NO'
        },
        horizontalMullion(item) {
            return (this.vModules(item) > 1 ? Number(item.width) / 12 : 0) > 0
                ? 'YES'
                : 'NO'
        },
        horizontalDivision(item) {
            return this.hModules(item) > 1 ? 'YES' : 'NO'
        },
        verticalDivision(item) {
            return this.vModules(item) > 1 ? 'YES' : 'NO'
        },
        openAreaPct(item) {
            const r = Number(item.openArea && item.openArea.openArea)
            return Number.isNaN(r) ? 0 : r * 100
        },
        formatLouverWeight(item) {
            const weight = getLouverWeightLbs(item)
            return weight == null || Number.isNaN(weight)
                ? '--'
                : Number(weight).toFixed(2)
        },
        formatPrice(item) {
            const price = this.getDisplayPrice(item)
            return price != null && price !== ''
                ? new Intl.NumberFormat('de-DE', {
                      style: 'currency',
                      currency: this.currency || 'USD',
                      maximumFractionDigits: 2,
                  }).format(price)
                : '--'
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: this.currency || 'USD',
                maximumFractionDigits: 2,
            }).format(value || 0)
        },
        async saveField(item, value) {
            try {
                const patch = buildCalculatorItemPatch(
                    item,
                    this.editing.field,
                    value != null ? value : this.editingValue,
                    this.quoteModel
                )
                this.loading = true
                this.editingItemId = item.id
                const updated = await API.updateCalculatorItem({
                    quoteId: this.quoteId,
                    itemId: item.id,
                    ...patch,
                })
                const idx = this.calculatorItems.findIndex(
                    i => i.id === item.id
                )
                if (idx >= 0) {
                    this.$set(this.calculatorItems, idx, { ...updated })
                }
                this.cancelEdit()
            } catch (error) {
                console.error('Error updating calculator item:', error)
                this.$emit('error', error)
                this.alertType = 'error'
                this.alertMessage = error.message || 'Error updating item.'
                this.activateAlert = true
            } finally {
                this.loading = false
                this.editingItemId = null
            }
        },
        async saveRow(form) {
            const item = this.rowEditItem
            try {
                const patch = buildCalculatorItemRowPatch(
                    item || {},
                    form,
                    this.quoteModel
                )
                this.rowSaving = true
                if (!item) {
                    const created = await API.createCalculatorItem({
                        quoteId: this.quoteId,
                        ...patch,
                    })
                    this.calculatorItems.push({ ...created })
                } else {
                    const updated = await API.updateCalculatorItem({
                        quoteId: this.quoteId,
                        itemId: item.id,
                        ...patch,
                    })
                    const idx = this.calculatorItems.findIndex(
                        i => i.id === item.id
                    )
                    if (idx >= 0) {
                        this.$set(this.calculatorItems, idx, { ...updated })
                    }
                }
                this.rowEditShow = false
                this.rowEditItem = null
            } catch (error) {
                console.error('Error saving calculator item:', error)
                this.$emit('error', error)
                this.alertType = 'error'
                this.alertMessage = error.message || 'Error saving item.'
                this.activateAlert = true
            } finally {
                this.rowSaving = false
            }
        },
        closeDialog() {
            this.rowEditShow = false
            this.rowEditItem = null
            this.deleteConfirmShow = false
            this.pendingDeleteItem = null
            this.scheduleMessageShow = false
            this.$emit('input', false)
            this.$emit('close')
        },
        triggerFileInput() {
            this.$refs.fileInput.click()
        },
        downloadTemplate() {
            try {
                const templateData = [
                    EXCEL_HEADERS,
                    ...this.calculatorItems.map(item =>
                        templateRowFromItem(item, this.quoteModel)
                    ),
                ]

                const worksheet = XLSX.utils.aoa_to_sheet(templateData)

                worksheet['!cols'] = EXCEL_HEADERS.map(() => ({ wch: 16 }))
                worksheet['!cols'][0] = { wch: 28 }

                const workbook = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Template')

                XLSX.writeFile(workbook, 'Calculator_Items_Template.xlsx')
            } catch (error) {
                console.error('Error generating template:', error)
                this.alertType = 'error'
                this.alertMessage = 'Error generating template file.'
                this.activateAlert = true
            }
        },
        async handleFileUpload(event) {
            const file = event.target.files[0]
            if (!file) return

            try {
                this.uploadingFile = true
                await this.processXLSXFile(file)
                // Reset file input
                event.target.value = ''
            } catch (error) {
                this.$emit('error', error)
                this.alertType = 'error'
                this.alertMessage =
                    error.message ||
                    'Error processing XLSX file. Louver weight is required on every row.'
                this.activateAlert = true
            } finally {
                this.uploadingFile = false
                this.importProgress = null
            }
        },
        async processXLSXFile(file) {
            const data = await this.readFileAsArrayBuffer(file)
            const workbook = XLSX.read(data, { type: 'array' })
            const sheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[sheetName]
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
            })
            const parsed = parseCalculatorItemsSheet(jsonData)
            const items = applyCalculatedOpenArea(parsed, this.quoteModel)

            const hasPrice = items.some(
                i => i.fixedNetUnitPrice != null || i.netEachPrice != null
            )
            const pendingSubtotal = hasPrice
                ? items.reduce((sum, i) => {
                      const price =
                          i.fixedNetUnitPrice != null
                              ? i.fixedNetUnitPrice
                              : i.netEachPrice != null
                              ? i.netEachPrice
                              : 0
                      return sum + price * (i.louverQuantity || 0)
                  }, 0)
                : null

            const costsRef = this.costsSubtotal || 0
            if (
                hasPrice &&
                pendingSubtotal != null &&
                costsRef > 0 &&
                Math.abs(pendingSubtotal - costsRef) > 0.01
            ) {
                this.previewItems = items
                this.pendingModel = this.quoteModel
                this.previewSubtotal = pendingSubtotal
                this.xlsxMismatchDialog = true
            } else {
                await this.replaceItemsFromXLSX(items)
            }
        },
        cancelXlsxImport() {
            this.previewItems = []
            this.previewSubtotal = 0
            this.pendingModel = null
            this.xlsxMismatchDialog = false
        },
        async confirmXlsxImport() {
            this.xlsxMismatchDialog = false
            try {
                this.uploadingFile = true
                await this.replaceItemsFromXLSX(this.previewItems)
            } finally {
                this.uploadingFile = false
                this.importProgress = null
                this.previewItems = []
                this.previewSubtotal = 0
                this.pendingModel = null
            }
        },
        async replaceItemsFromXLSX(items) {
            this.loading = true
            try {
                const importBatchId = `${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2, 8)}`
                this.importProgress = { current: 0, total: items.length }
                for (let i = 0; i < items.length; i += IMPORT_CHUNK_SIZE) {
                    const slice = items.slice(i, i + IMPORT_CHUNK_SIZE)
                    await API.replaceCalculatorItems({
                        quoteId: this.quoteId,
                        items: slice,
                        importBatchId,
                        offset: i,
                        done: i + slice.length >= items.length,
                    })
                    this.importProgress = {
                        current: i + slice.length,
                        total: items.length,
                    }
                    await new Promise(resolve => setTimeout(resolve, 0))
                }
                this.nameFilter = ''
                this.page = 1
                await this.loadCalculatorItems()
                this.alertType = 'success'
                this.alertMessage = `Replaced with ${this.calculatorItems.length} item(s).`
                this.activateAlert = true
            } catch (error) {
                console.error('Error replacing items:', error)
                throw error
            } finally {
                this.loading = false
                this.editingItemId = null
            }
        },
        readFileAsArrayBuffer(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = e => resolve(e.target.result)
                reader.onerror = e => reject(e)
                reader.readAsArrayBuffer(file)
            })
        },
    },
}
</script>

<style scoped>
.name-filter {
    max-width: 20rem;
}
.calculator-items-table {
    overflow-x: auto;
}
.calculator-items-table table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}
.calculator-items-table th,
.calculator-items-table td {
    border: thin solid #eeeeee;
    padding: 6px 8px;
    text-align: center;
    white-space: nowrap;
}
.calculator-items-table .text-left {
    text-align: left;
}
.calculator-items-table thead th {
    background: #f5f5f5;
    font-weight: 600;
}
.calculator-items-table .schedule-title {
    font-size: 16px;
    letter-spacing: 0.04em;
}
.calculator-items-table .schedule-summary th,
.calculator-items-table .schedule-groups th {
    font-weight: 600;
}
.calculator-items-table .schedule-message-cell {
    white-space: normal;
    text-align: left;
    min-width: 16rem;
}
</style>
